import React, { useState, useEffect, useCallback } from 'react';
import { X, Bell, CheckCircle2, AlertCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  requestNotificationPermission,
  subscribeToPush,
  unsubscribePush,
  getIsPushSubscribed,
} from '../utils/pushNotification';

const NotificationSettingsModal = ({ isOpen, onClose, userRole = 'user', userId = null, email = null }) => {
  const [permission, setPermission] = useState('default');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const refreshStatus = useCallback(async () => {
    if ('Notification' in window) {
      setPermission(Notification.permission);
    }
    setIsSubscribed(await getIsPushSubscribed());
  }, []);

  useEffect(() => {
    if (isOpen) {
      refreshStatus();
    }
  }, [isOpen, refreshStatus]);

  const handleEnableNotifications = async () => {
    setIsLoading(true);
    try {
      const result = await requestNotificationPermission();
      setPermission(result);

      if (result === 'granted') {
        await subscribeToPush({ role: userRole, userId, email });
        setIsSubscribed(true);
      }
    } catch (err) {
      console.error('Bildirim izni hatası:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisableNotifications = async () => {
    setIsLoading(true);
    try {
      await unsubscribePush();
      setIsSubscribed(false);
      // Tarayıcı izni hâlâ granted kalır — bu platform kısıtı
      if ('Notification' in window) {
        setPermission(Notification.permission);
      }
    } catch (err) {
      console.error('Bildirim kapatma hatası:', err);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  const isActive = isSubscribed && permission === 'granted';

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.2 }}
          className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden border border-[#D4AF37]/20"
        >
          <div className="bg-gradient-to-r from-[#FAF7F0] to-white p-6 border-b border-[#D4AF37]/20">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-[#D4AF37]/10 flex items-center justify-center">
                  <Bell className="w-6 h-6 text-[#D4AF37]" />
                </div>
                <h2 className="text-xl font-bold text-[#1a1a1a] font-['Playfair_Display']">
                  Bildirim Ayarları
                </h2>
              </div>
              <button
                onClick={onClose}
                className="w-10 h-10 rounded-xl hover:bg-gray-100 flex items-center justify-center transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {isActive && (
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-2xl">
                  <CheckCircle2 className="w-6 h-6 text-green-600 shrink-0" />
                  <div>
                    <p className="font-bold text-green-900 text-sm">Bildirimler Aktif</p>
                    <p className="text-xs text-green-700">Sipariş güncellemelerini alacaksınız</p>
                  </div>
                </div>
                <button
                  onClick={handleDisableNotifications}
                  disabled={isLoading}
                  className="w-full py-3 px-4 bg-red-50 hover:bg-red-100 text-red-700 rounded-xl font-bold text-sm transition-colors border border-red-200"
                >
                  {isLoading ? 'Kapatılıyor...' : '🔕 Bildirimleri Kapat'}
                </button>
              </div>
            )}

            {!isActive && permission === 'denied' && (
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-2xl">
                  <AlertCircle className="w-6 h-6 text-red-600 shrink-0" />
                  <div>
                    <p className="font-bold text-red-900 text-sm">Bildirimler Reddedildi</p>
                    <p className="text-xs text-red-700">
                      Tarayıcı güvenlik kuralı gereği reddedilen izin tekrar sorulmaz. Modalı kapatıp açmak da izin penceresini geri getirmez.
                    </p>
                  </div>
                </div>
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-2xl text-sm text-gray-700 space-y-2">
                  <p className="font-bold text-amber-900">📱 Bildirimleri Açmak İçin:</p>
                  <p className="text-xs leading-relaxed">
                    <strong>iOS:</strong> Ayarlar → Safari → Bildirimler → carmengold.com → İzin Ver
                  </p>
                  <p className="text-xs leading-relaxed">
                    <strong>Android:</strong> Ayarlar → Uygulamalar → Chrome → İzinler → Bildirimler → İzin Ver
                  </p>
                </div>
              </div>
            )}

            {!isActive && permission !== 'denied' && (
              <div className="space-y-4">
                <div className="p-4 bg-[#FAF7F0] border border-[#D4AF37]/30 rounded-2xl">
                  <p className="text-sm text-gray-700 leading-relaxed">
                    {permission === 'granted'
                      ? 'Push aboneliğiniz kapatıldı. Tekrar açmak için aşağıdaki butona dokunun (izin zaten verilmiş, pencere çıkmayabilir).'
                      : 'Henüz izin vermediniz. Zile veya aşağıdaki butona her dokunduğunuzda tarayıcı izin penceresi tekrar sorulur.'}
                  </p>
                </div>
                <button
                  onClick={handleEnableNotifications}
                  disabled={isLoading}
                  className="w-full py-3 px-4 bg-[#D4AF37] hover:bg-[#b8941f] text-white rounded-xl font-bold text-sm transition-colors flex items-center justify-center gap-2"
                >
                  <Bell className="w-5 h-5" />
                  {isLoading ? 'Açılıyor...' : '🔔 Bildirimleri Aç'}
                </button>
                {permission === 'granted' && (
                  <p className="text-xs text-gray-500 text-center leading-relaxed">
                    Tarayıcı izni zaten verilmiş; tekrar izin penceresi çıkmayabilir. Bildirimler yine de yeniden etkinleştirilir.
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="p-4 bg-gray-50 border-t border-gray-100">
            <p className="text-xs text-gray-500 text-center">
              Bildirim ayarlarını istediğiniz zaman değiştirebilirsiniz
            </p>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default NotificationSettingsModal;
